/**
 * The card's first step, "Clarify the want" (Requests & Agreements; PRD-delegation-module.md
 * 4.1): who wants it, what is wanted, what it gets them. Saved as a Desired Outcome Brief,
 * which already asks those questions, and cited as in play on the request, so the request
 * holds no second copy of them.
 */
import { useDesiredOutcomeStore } from '@/stores/prioritization/desiredOutcomeStore';
import type { AssignmentGround } from './assignmentService';

export interface WantDraft { who: string; what: string; getsThem: string }
export const NO_WANT: WantDraft = { who: '', what: '', getsThem: '' };

/**
 * Save the want as a Desired Outcome Brief and return it as a ground to cite. Nothing is
 * saved when the three answers are blank. `discard` takes it back if the request fails.
 */
export async function saveWant(w: WantDraft, met: string): Promise<{ ground: AssignmentGround | null; discard: () => Promise<void>; error?: string }> {
  const nothing = { ground: null, discard: async () => {} };
  if (!w.who.trim() && !w.what.trim() && !w.getsThem.trim()) return nothing;
  const store = useDesiredOutcomeStore.getState();
  const title = w.what.trim() || 'What is wanted';
  const { outcome, error } = await store.createOutcome({
    title,
    who: w.who.trim(),
    whatWanted: w.what.trim(),
    deeperBenefit: w.getsThem.trim() || null,
    evidenceSufficient: met.trim() || null,
  });
  if (!outcome) return { ...nothing, error: error ?? 'The brief could not be saved.' };
  return {
    ground: { kind: 'desired_outcome_brief', id: outcome.id, label: title },
    discard: async () => { await store.deleteOutcome(outcome.id); },
  };
}

